import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import SSOCallback from './pages/SSOCallback';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { PhoneProvider, PhoneRoot, PopoutPhoneWindow } from './features/telephony';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';

// Keep Login eager — it is the landing page for unauthenticated users and
// loading a spinner-then-login is a worse first impression than just
// showing the login form immediately.
// Everything else is code-split so /login and /quote/sign/* do not
// download the entire authenticated-app bundle.
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const SetPassword = lazy(() => import('./pages/SetPassword'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Customers = lazy(() => import('./pages/Customers'));
const Companies = lazy(() => import('./pages/Companies'));
const Quotes = lazy(() => import('./pages/Quotes'));
const QuoteEditor = lazy(() => import('./pages/QuoteEditor'));
const QuoteSign = lazy(() => import('./pages/QuoteSign'));
const QuoteCampaignSign = lazy(() => import('./pages/QuoteCampaignSign'));
const QuoteCampaigns = lazy(() => import('./pages/QuoteCampaigns'));
const QuoteCampaignEditor = lazy(() => import('./pages/QuoteCampaignEditor'));
const QuoteCampaignDetail = lazy(() => import('./pages/QuoteCampaignDetail'));
const Deal = lazy(() => import('./pages/Deal'));
const Profile = lazy(() => import('./pages/Profile'));
const Settings = lazy(() => import('./pages/Settings'));
const Templates = lazy(() => import('./pages/Templates'));
const TemplateEditor = lazy(() => import('./pages/TemplateEditor'));
const Calendar = lazy(() => import('./pages/Calendar'));
const Marketing = lazy(() => import('./pages/Marketing'));
const SalesReport = lazy(() => import('./pages/SalesReport'));
const GoalsReport = lazy(() => import('./pages/GoalsReport'));
const TextmeSMS = lazy(() => import('./pages/TextmeSMS'));
const CustomerActivity = lazy(() => import('./pages/customer/CustomerPage'));
const CompanyActivity = lazy(() => import('./pages/company/CompanyPage'));
const Inbox = lazy(() => import('./pages/Inbox'));
const Calls = lazy(() => import('./pages/Calls'));
const CallsReport = lazy(() => import('./pages/CallsReport'));
const ShopifyOverview = lazy(() => import('./pages/Shopify/Overview'));
const ShopifyAppDetail = lazy(() => import('./pages/Shopify/AppDetail'));
const ShopifyReferrals = lazy(() => import('./pages/Shopify/Referrals'));

// Minimal RTL-friendly fallback shown while a route chunk loads.
const RouteFallback: React.FC = () => (
  <div
    style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      direction: 'rtl',
      fontFamily: 'inherit',
      color: '#555',
    }}
  >
    טוען…
  </div>
);

function App() {
  // AUDIT-071: wrap the whole tree in ErrorBoundary so a render crash in
  // any page / chart / editor shows a recovery screen (+ Sentry capture)
  // instead of blanking the root.
  return (
    <ErrorBoundary>
    <AuthProvider>
        <ToastProvider>
          <Router
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true
            }}
          >
            <PhoneProvider>
            <div className="App">
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                <Route path="/login" element={<Login />} />
                {/* SSO landing page — consumes tokens from URL fragment,
                    hydrates AuthContext, redirects to /dashboard. */}
                <Route path="/sso-callback" element={<SSOCallback />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                {/* Popout call controls. Renders only mirrored state from
                    BroadcastChannel — it holds no session and fetches
                    nothing, so it needs no auth of its own. */}
                <Route path="/phone-popout" element={<PopoutPhoneWindow />} />
                <Route path="/set-password" element={<SetPassword />} />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/customers"
                  element={
                    <ProtectedRoute>
                      <Customers />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/customers/:customerId"
                  element={
                    <ProtectedRoute>
                      <CustomerActivity />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/customers/:customerId/activity"
                  element={
                    <ProtectedRoute>
                      <CustomerActivity />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/companies"
                  element={
                    <ProtectedRoute>
                      <Companies />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/companies/:companyId"
                  element={
                    <ProtectedRoute>
                      <CompanyActivity />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/companies/:companyId/activity"
                  element={
                    <ProtectedRoute>
                      <CompanyActivity />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/quotes"
                  element={
                    <ProtectedRoute>
                      <Quotes />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/quote-campaigns"
                  element={<ProtectedRoute><QuoteCampaigns /></ProtectedRoute>}
                />
                <Route
                  path="/quote-campaigns/new"
                  element={<ProtectedRoute><QuoteCampaignEditor /></ProtectedRoute>}
                />
                <Route
                  path="/quote-campaigns/:id/edit"
                  element={<ProtectedRoute><QuoteCampaignEditor /></ProtectedRoute>}
                />
                <Route
                  path="/quote-campaigns/:id"
                  element={<ProtectedRoute><QuoteCampaignDetail /></ProtectedRoute>}
                />
                <Route
                  path="/quotes/new"
                  element={
                    <ProtectedRoute>
                      <QuoteEditor />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/quotes/:id"
                  element={
                    <ProtectedRoute>
                      <QuoteEditor />
                    </ProtectedRoute>
                  }
                />
                {/* How the customer will see it. Staff only: no view is recorded and
                    the status does not change, unlike opening the customer link. */}
                <Route
                  path="/quotes/:id/preview"
                  element={
                    <ProtectedRoute>
                      <QuoteSign />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/quotes/:id/edit"
                  element={
                    <ProtectedRoute>
                      <QuoteEditor />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/deals/:id"
                  element={
                    <ProtectedRoute>
                      <Deal />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/calendar"
                  element={
                    <ProtectedRoute>
                      <Calendar />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/inbox"
                  element={
                    <ProtectedRoute>
                      <Inbox />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/calls"
                  element={
                    <ProtectedRoute>
                      <Calls />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/calls/report"
                  element={
                    <ProtectedRoute>
                      <CallsReport />
                    </ProtectedRoute>
                  }
                />
                {/* Shopify Partner revenue dashboard — top-level MRR + drill-down per app. */}
                <Route
                  path="/shopify"
                  element={
                    <ProtectedRoute>
                      <ShopifyOverview />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/shopify/referrals"
                  element={
                    <ProtectedRoute>
                      <ShopifyReferrals />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/shopify/apps/:handle"
                  element={
                    <ProtectedRoute>
                      <ShopifyAppDetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/marketing"
                  element={
                    <ProtectedRoute requireSuperAdmin={true}>
                      <Marketing />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/marketing/template/:templateId"
                  element={
                    <ProtectedRoute requireSuperAdmin={true}>
                      <Marketing />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute requireSuperAdmin={true}>
                      <Settings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/templates"
                  element={
                    <ProtectedRoute>
                      <Templates />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/templates/new"
                  element={
                    <ProtectedRoute>
                      <TemplateEditor />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/templates/:id"
                  element={
                    <ProtectedRoute>
                      <TemplateEditor />
                    </ProtectedRoute>
                  }
                />
                <Route path="/quote/sign/:shareId" element={<QuoteSign />} />
                <Route path="/quote/:shareId" element={<QuoteSign />} />
                {/* Links the public API handed out before it pointed at /quote/sign. */}
                <Route path="/quotes/share/:shareId" element={<QuoteSign />} />
                {/* Public sign page for "send once, sign many" campaigns.
                    Accepts an optional per-recipient tracking token so
                    /q/<shortShareId>/<token> attributes open + sign back
                    to the specific dispatch row. */}
                <Route path="/q/:shareId" element={<QuoteCampaignSign />} />
                <Route path="/q/:shareId/:recipientToken" element={<QuoteCampaignSign />} />
                <Route
                  path="/sales-report"
                  element={
                    <ProtectedRoute>
                      <SalesReport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/goals-report"
                  element={
                    <ProtectedRoute>
                      <GoalsReport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/textme"
                  element={
                    <ProtectedRoute>
                      <TextmeSMS />
                    </ProtectedRoute>
                  }
                />
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </Suspense>
            </div>
            {/* Global softphone surfaces (call bar, incoming popup, dialer) */}
            <PhoneRoot />
            </PhoneProvider>
          </Router>
        </ToastProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
